import {
  Card,
  Col,
  notification,
  Row,
  Switch,
  Radio,
  Spin,
  Modal,
  Drawer,
  Collapse,
  DatePicker,
  Empty,
} from "antd";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import map_locationIcon from "../../assets/images/restaurant/map_locationIcon.svg";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import AddressManager from "../../components/AddressManager";
import FooterDisplay from "../../components/footer";
import { ReactComponent as Cancel } from "../../assets/images/icons/x.svg";
import {
  _get_saved_address,
  _get_user_wallet,
  _single_business_data,
  _calculate_restaurant_order_price,
  _placeMenuOrder,
  _get_profile,
  _user_dashboard_shop,
  _get_fourward_account,
} from "../../common/axios_services";
import PromoBanner from "../../components/PromoBanner";
import AppRoutes from "../../common/routes";
import { useDispatch, useSelector } from "react-redux";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import NumberFormat from "react-number-format";
import {
  setRestaurant,
  clearCart,
  changeQuantity,
} from "../../slices/restaurantCartSlice";
import TopUpWallet from "./TopUpWallet";
import RequestTransactionCode from "../../components/profile/transcode";
import BackToRestaurant from "./BackToRestaurant";
import { roundDisplayPrice } from "../../utils/priceHelpers";
import { normalizeBusinessType } from "../../utils/businessTypeHelpers";
import HelpSupportButton from "../../components/HelpSupportButton";

const DELIVERY_TYPES = {
  PICKUP: "pickup",
  DELIVERY: "delivery",
};

const ReviewCheckout = ({ businessType }) => {
  const navigate = useNavigate();
  const [visibleDrawer, setVisibleDrawer] = useState(false);
  const [allDeliveryLocations, setAllDeliveryLocations] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState("standard");
  const [loading, setLoading] = useState(true);
  const [restaurantData, setRestaurantData] = useState({});
  const { id: businessId } = useParams();
  const [userTransferData, setUserTransferData] = useState({});
  const [paymentMethod, setPaymentMethod] = useState("WALLET");
  const [fourwardBalance, setFourwardBalance] = useState(0);
  const restaurantCarts = useSelector((store) => store.restaurantCart.items);
  const dispatch = useDispatch();
  const location = useLocation();
  const { useAutoDiscount } = location.state || {};
  const query = new URLSearchParams(location.search);
  const checkoutDeliveryType = query.get("deliveryType");
  const queryDeliveryMode = query.get("deliveryMode");

  const initialDeliveryMode =
    queryDeliveryMode?.toLowerCase() === "delivery"
      ? DELIVERY_TYPES.DELIVERY
      : DELIVERY_TYPES.PICKUP;

  const [sameDayDeliveryMode, setSameDayDeliveryMode] =
    useState(initialDeliveryMode);
  const [nextDayDeliveryMode, setNextDayDeliveryMode] =
    useState(initialDeliveryMode);
  const [nextDayDeliveryDate, setNextDayDeliveryDate] = useState(null);
  const [orderFulfillmentTiming, setOrderFulfillmentTiming] = useState(
    businessType === "restaurant" ? "same_day" : "next_day",
  );

  const bucketOf = (item) => {
    if (businessType !== "restaurant") return "next_day";
    return (item?.detail?.deliveryDateType ||
      item?.deliveryDateType ||
      orderFulfillmentTiming) === "same_day"
      ? "same_day"
      : "next_day";
  };

  const activeCartItems = React.useMemo(() => {
    if (!checkoutDeliveryType) return restaurantCarts;
    return restaurantCarts.filter((i) => bucketOf(i) === checkoutDeliveryType);
  }, [
    restaurantCarts,
    checkoutDeliveryType,
    orderFulfillmentTiming,
    businessType,
  ]);

  const hasSameDayItems = activeCartItems.some(
    (i) => bucketOf(i) === "same_day",
  );
  const hasNextDayItems = activeCartItems.some(
    (i) => bucketOf(i) === "next_day",
  );
  const showPickupAddress =
    (hasSameDayItems && sameDayDeliveryMode === DELIVERY_TYPES.PICKUP) ||
    (hasNextDayItems && nextDayDeliveryMode === DELIVERY_TYPES.PICKUP);
  const showDeliveryAddress =
    (hasSameDayItems && sameDayDeliveryMode === DELIVERY_TYPES.DELIVERY) ||
    (hasNextDayItems && nextDayDeliveryMode === DELIVERY_TYPES.DELIVERY);

  const [completeOrderEnabled, setCompleteOrderEnabled] = useState(false);
  const [displayTransCodeModal, setDisplayTransCodeModal] = useState(false);
  const [userPin, setUserPin] = useState("");
  const [otpCounter, setOtpCounter] = useState(false);
  const [calculatedOrderPrice, setCalculatedOrderPrice] = useState({});
  const serviceFee =
    calculatedOrderPrice?.serviceFee ??
    calculatedOrderPrice?.breakdown?.serviceFee ??
    0;
  const [recalcTrigger, setRecalcTrigger] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [activeCollapseKeys, setActiveCollapseKeys] = useState([
    "same_day",
    "next_day",
  ]);
  const [formattedSelectedDate, setFormattedSelectedDate] = useState("");
  const [userHasPin, setUserHasPin] = useState(null);
  const [weeklyOrderCount, setWeeklyOrderCount] = useState(0);
  const [rateConfig, setRateConfig] = useState(null);
  const [showDistanceCapModal, setShowDistanceCapModal] = useState(false);
  const [deliveryRestrictionType, setDeliveryRestrictionType] =
    useState("distance");
  const hasShownFreeDeliveryTermsModal = React.useRef(false);

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;
  const showDrawer = () => setVisibleDrawer(true);
  const closeDrawer = () => setVisibleDrawer(false);

  const openNotificationWithIcon = (type, message, description) => {
    const validTypes = ["success", "error", "warning", "info"];
    const targetType = validTypes.includes(type) ? type : "error";
    const headerTitle = description
      ? message
      : validTypes.includes(type)
        ? targetType.charAt(0).toUpperCase() + targetType.slice(1)
        : type;
    const bodyDesc = description || message;

    if (notification && typeof notification[targetType] === "function") {
      notification[targetType]({
        message: headerTitle,
        description: bodyDesc,
      });
    }
  };

  const handleModalOk = () => {
    setIsModalVisible(false);
    setSameDayDeliveryMode(DELIVERY_TYPES.PICKUP);
    setNextDayDeliveryMode(DELIVERY_TYPES.PICKUP);
  };

  const fetchRestaurantInfo = async (id) => {
    try {
      const response = await _single_business_data({
        id,
        type: businessType || "restaurant",
      });
      setRestaurantData(response.data.data);
      setLoading(false);
    } catch (err) {
      if (err.response) {
        if (err.response.data.message === "Unauthorized") {
          localStorage.removeItem("konnect_token");
          navigate("/");
        }
        openNotificationWithIcon("error", err.response.data.message);
      } else {
        openNotificationWithIcon("Something went wrong", err.message);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    if (businessId) {
      fetchRestaurantInfo(businessId);
      dispatch(setRestaurant({ restaurantId: businessId, businessType }));
    }
  }, [businessId, dispatch, businessType]);

  useEffect(() => {
    if (activeCartItems.length > 0) {
      calculateMenuPrice();
    }
  }, [
    activeCartItems,
    sameDayDeliveryMode,
    nextDayDeliveryMode,
    nextDayDeliveryDate,
    selectedAddress,
    orderFulfillmentTiming,
    recalcTrigger,
  ]);

  const fetchUserWallet = async () => {
    try {
      const user_profile = await _get_user_wallet();
      let userData = user_profile?.data.data;
      setUserTransferData({ ...userData, balance: userData?.availableBalance });
      setLoading(false);
    } catch (err) {
      if (err.response) {
        if (err.response.data.message === "Unauthorized") {
          localStorage.removeItem("konnect_token");
          navigate(`/signin`);
        }
      } else {
      }
    }
  };

  const fetchFourwardBalance = async () => {
    try {
      const response = await _get_fourward_account();
      const data = response?.data?.data || response?.data || {};
      setFourwardBalance(Number(data?.account?.apvBalance || 0));
    } catch (err) {
      setFourwardBalance(0);
    }
  };

  const fetchDashBoardShop = async () => {
    try {
      const res = await _user_dashboard_shop();
      const data = res?.data?.data?.data || res?.data?.data;
      setWeeklyOrderCount(data?.weekly_orders || 0);
      setRateConfig(data?.config || null);
    } catch (err) {}
  };

  const fetchUserAddresses = async () => {
    try {
      const res = await _get_saved_address();
      if (res?.data?.data?.data || res?.data?.data) {
        const addresses = res?.data?.data?.data || res?.data?.data;
        setAllDeliveryLocations(addresses);

        if (addresses.length > 0) {
          const lastUsedId = localStorage.getItem(
            "konnect_last_selected_address_id",
          );
          const lastUsedAddr = lastUsedId
            ? addresses.find((addr) => String(addr.id) === String(lastUsedId))
            : null;
          const defaultAddr = addresses.find((addr) => addr.isDefault);
          const selected = lastUsedAddr || defaultAddr || addresses[0];
          setSelectedAddress(selected.id);
          localStorage.setItem("konnect_last_selected_address_id", selected.id);
        }
      }
    } catch (err) {}
  };

  const getFormattedOrderData = (transactionPin) => {
    const orderItems = [];

    const defaultDeliveryDateType =
      businessType !== "restaurant"
        ? "next_day"
        : hasNextDayItems || nextDayDeliveryDate
          ? "next_day"
          : "same_day";

    activeCartItems.forEach((item) => {
      const price = parseFloat(item?.detail?.public_price) || 0;
      const quantity = item?.quantity || 1;
      const deliveryDateType = defaultDeliveryDateType;

      const extras = item.extras?.length
        ? item.extras
            .filter(
              (extra) =>
                extra?.id !== "container_cost" &&
                extra?.item?.name !== "Container Cost" &&
                extra?.name !== "Container Cost",
            )
            .map((extra) => ({
              id: extra?.id || "",
              price: parseFloat(extra?.price) || 0,
              quantity: extra?.quantity || 1,
              item: extra?.item || { name: extra?.name || "Extra" },
            }))
        : [];

      orderItems.push({
        menuItemId: item?.productId || "",
        quantity: quantity,
        price: price,
        deliveryDateType: deliveryDateType,
        extras: extras,
      });
    });

    return {
      businessId: businessId,
      businessType: businessType,
      orderItems: orderItems,
      same_day: {
        deliveryType: sameDayDeliveryMode,
      },
      next_day: {
        deliveryType: nextDayDeliveryMode,
        deliveryDate: nextDayDeliveryDate,
      },
      savedAddressId:
        sameDayDeliveryMode === DELIVERY_TYPES.DELIVERY ||
        nextDayDeliveryMode === DELIVERY_TYPES.DELIVERY
          ? selectedAddress
          : undefined,
      transactionPin: transactionPin,
      useAutoDiscount: !!useAutoDiscount,
      paymentMethod,
    };
  };

  const calculateMenuPrice = async () => {
    try {
      const data = getFormattedOrderData();

      const isDelivery =
        sameDayDeliveryMode === DELIVERY_TYPES.DELIVERY ||
        nextDayDeliveryMode === DELIVERY_TYPES.DELIVERY;

      if (isDelivery && !selectedAddress) {
        return;
      }

      setLoading(true);
      const calculatedResult = await _calculate_restaurant_order_price(data);

      const calculatedData = calculatedResult?.data?.data || {};
      setCalculatedOrderPrice(calculatedData);

      const isDeliveryType =
        sameDayDeliveryMode === DELIVERY_TYPES.DELIVERY ||
        nextDayDeliveryMode === DELIVERY_TYPES.DELIVERY;
      const deliveryFee = calculatedData?.deliveryFee || 0;
      const deliveryDiscount = calculatedData?.deliveryDiscount || 0;

      const freeDeliveryThreshold =
        rateConfig?.freeDeliveryValue ||
        rateConfig?.restaurantFdValue ||
        25000;
      const totalAmountForFreeDelivery =
        calculatedData?.breakdown?.subtotalWithMarkup || 0;
      const isEligibleForFreeDelivery =
        totalAmountForFreeDelivery >= freeDeliveryThreshold;

      if (calculatedData?.isDistanceLimitExceeded) {
        setDeliveryRestrictionType("distance");
        setShowDistanceCapModal(true);
      }
    } catch (err) {
      if (err.response) {
        openNotificationWithIcon(
          "error",
          err.response.data.message || "Error occurred",
        );
      } else {
        openNotificationWithIcon(
          "error",
          "Error",
          err.message || "Something went wrong",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOrder = async (transactionPin) => {
    try {
      setLoading(true);
      const orderData = getFormattedOrderData(transactionPin);
      const orderResult = await _placeMenuOrder(orderData);
      openNotificationWithIcon(
        "success",
        orderResult?.data?.title || "Order Successful",
        orderResult?.data?.message ||
          "Your order has been placed successfully.",
      );
      toggleTransCodeModalDisplay();
      activeCartItems.forEach((item) => {
        dispatch(changeQuantity({ productId: item.productId, quantity: 0 }));
      });

      const orderId = orderResult?.data?.data?.id;
      navigate(`/Restaurant/${orderId}/checkout_successful`);
    } catch (err) {
      if (err.response) {
        openNotificationWithIcon(
          "error",
          err.response.data.message || "Order failed",
        );
      } else {
        openNotificationWithIcon(
          "error",
          "Error",
          err.message || "Something went wrong",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const checkUserPinStatus = async () => {
    try {
      const profile = await _get_profile();
      setUserHasPin(profile?.data?.data?.hasTransactionPin || false);
    } catch (err) {
      setUserHasPin(false);
    }
  };

  useEffect(() => {
    fetchUserWallet();
    fetchFourwardBalance();
    checkUserPinStatus();
    fetchDashBoardShop();
    fetchUserAddresses();
  }, []);

  const totalItems = activeCartItems.reduce(
    (total, item) => total + item.quantity,
    0,
  );

  const totalAmount = activeCartItems.reduce((total, item) => {
    const rawPrice =
      parseFloat(item?.detail?.public_price || item?.detail?.priceWithMarkup) ||
      0;
    const price = roundDisplayPrice(rawPrice);
    let itemTotal = price * (item.quantity || 0);

    const extrasTotal = (item.extras || []).reduce((extraTotal, extra) => {
      if (
        extra?.id === "container_cost" ||
        extra?.item?.name === "Container Cost" ||
        extra?.name === "Container Cost"
      ) {
        return extraTotal;
      }
      const extraPrice = roundDisplayPrice(parseFloat(extra?.price) || 0);
      const extraQuantity = parseFloat(extra?.quantity) || 0;
      return extraTotal + extraPrice * extraQuantity;
    }, 0);
    itemTotal += extrasTotal;

    return total + itemTotal;
  }, 0);

  const getValidationStatus = () => {
    if (
      userTransferData?.balance === undefined ||
      calculatedOrderPrice?.total === undefined
    ) {
      return {
        valid: false,
        reason: "incomplete",
        message: "Loading order data...",
      };
    }

    const hasNextDayItems = activeCartItems.some(
      (item) =>
        (item?.detail?.deliveryDateType ||
          item?.deliveryDateType ||
          (businessType === "restaurant" ? "same_day" : "next_day")) ===
        "next_day",
    );

    if (hasNextDayItems && !nextDayDeliveryDate) {
      return {
        valid: false,
        reason: "incomplete",
        message: "Select delivery date for next day items",
      };
    }

    const hasDelivery =
      sameDayDeliveryMode === DELIVERY_TYPES.DELIVERY ||
      nextDayDeliveryMode === DELIVERY_TYPES.DELIVERY;

    if (hasDelivery && selectedAddress === null) {
      return {
        valid: false,
        reason: "incomplete",
        message: "Add delivery address",
      };
    }

    if (
      hasDelivery &&
      (calculatedOrderPrice?.deliveryFee === undefined ||
        calculatedOrderPrice?.deliveryFee === null)
    ) {
      return {
        valid: false,
        reason: "incomplete",
        message: "Unable to compute delivery fee",
      };
    }

    const isWholesale = normalizeBusinessType(businessType) === "wholesale";
    if (
      (nextDayDeliveryMode === DELIVERY_TYPES.DELIVERY || isWholesale) &&
      hasNextDayItems &&
      !nextDayDeliveryDate
    ) {
      return {
        valid: false,
        reason: "incomplete",
        message: "Select delivery date",
      };
    }

    const isBalanceSufficient =
      userTransferData.balance >=
      (calculatedOrderPrice?.breakdown?.finalTotal || 0);

    if (!isBalanceSufficient) {
      return {
        valid: false,
        reason: "insufficient_balance",
        message: "Insufficient balance",
      };
    }

    return { valid: true, reason: "complete" };
  };

  React.useEffect(() => {
    const status = getValidationStatus();
    setCompleteOrderEnabled(status.valid);
  }, [
    sameDayDeliveryMode,
    nextDayDeliveryMode,
    nextDayDeliveryDate,
    selectedAddress,
    userTransferData?.balance,
    calculatedOrderPrice?.breakdown?.finalTotal,
  ]);

  const toggleTransCodeModalDisplay = () => {
    const status = getValidationStatus();
    if (!status.valid) {
      if (status.reason === "insufficient_balance") {
        notification.error({
          message: "Insufficient Balance",
          description: `You need ₦${parseFloat(
            roundDisplayPrice(
              calculatedOrderPrice?.breakdown?.finalTotal || 0,
            ) - (userTransferData?.balance || 0),
          ).toFixed(2)} more to complete this order.`,
          duration: 3,
        });
      } else if (status.message === "Select delivery date for next day items") {
        openNotificationWithIcon(
          "warning",
          "Select delivery date for next day items",
        );
      } else {
        notification.warning({
          message: "Incomplete Information",
          description: status.message,
          duration: 3,
        });
      }
      return;
    }
    setDisplayTransCodeModal(!displayTransCodeModal);
    setOtpCounter(true);
  };

  const verifyUserPin = async (pin) => {
    setLoading(true);
    setDisplayTransCodeModal(false);
    try {
      await handleMenuOrder(pin);
      setLoading(false);
    } catch (err) {
      if (err?.response?.data?.message) {
        if (err?.code === "ERR_NETWORK") {
          openNotificationWithIcon(
            "error",
            "An error occurred while placing order. Please check network and try again",
          );
        } else if (err?.response?.data?.message === "Unauthorized") {
          openNotificationWithIcon(
            "error",
            "User Authorization expired. Please log in to continue",
          );
          navigate("/signin");
        } else {
          openNotificationWithIcon(
            "error",
            err?.data?.message.length
              ? err?.data?.message
              : err?.response?.data?.message,
          );
        }
      } else {
        openNotificationWithIcon(
          "error",
          "An error occurred while placing order. Please try again",
        );
      }
      setLoading(false);
    }
  };

  const OrderItemsSection = () => {
    const groupedItems = {
      same_day: [],
      next_day: [],
    };

    activeCartItems.forEach((item) => {
      const deliveryType =
        item?.detail?.deliveryDateType ||
        item?.deliveryDateType ||
        (businessType === "restaurant" ? "same_day" : "next_day");
      const finalGroup = deliveryType === "same_day" ? "same_day" : "next_day";
      groupedItems[finalGroup].push(item);
    });

    const handleSameDayDeliveryChange = (e) => {
      setSameDayDeliveryMode(e.target.value);
    };

    const handleNextDayDeliveryChange = (e) => {
      setNextDayDeliveryMode(e.target.value);
    };

    const renderItemList = (items) => {
      return items.map((item, index) => {
        const rawPrice =
          parseFloat(
            item?.detail?.public_price || item?.detail?.priceWithMarkup,
          ) || 0;
        const originalPrice = roundDisplayPrice(rawPrice);

        return (
          <React.Fragment key={index}>
            <div className="py-4">
              <div className="flex justify-between items-start gap-4">
                <div className="flex gap-3 flex-1">
                  <p className="text-xl font-medium text-gray-700 whitespace-nowrap">
                    {item.quantity}x
                  </p>
                  <p className="text-2xl font-medium text-gray-900">
                    {item.detail?.product_name}
                  </p>
                </div>
                <p className="text-2xl font-semibold text-[#44843F] whitespace-nowrap">
                  <NumberFormat
                    value={originalPrice * item.quantity}
                    displayType={"text"}
                    thousandSeparator={true}
                    decimalScale={2}
                    fixedDecimalScale={true}
                    prefix={"₦"}
                  />
                </p>
              </div>

              {item.extras &&
                item.extras.filter(
                  (e) =>
                    e.id !== "container_cost" &&
                    e.item?.name !== "Container Cost" &&
                    e.name !== "Container Cost",
                ).length > 0 && (
                  <div className="mt-3 ml-8 space-y-2">
                    <p className="text-xl font-semibold text-gray-600">
                      Extras:
                    </p>
                    {item.extras
                      .filter(
                        (e) =>
                          e.id !== "container_cost" &&
                          e.item?.name !== "Container Cost" &&
                          e.name !== "Container Cost",
                      )
                      .map((extra, extraIndex) => (
                        <div
                          key={extraIndex}
                          className="flex justify-between items-center gap-4"
                        >
                          <p className="text-xl text-gray-600">
                            {extra.name} (x{extra.quantity})
                          </p>
                          <p className="text-xl font-medium text-[#44843F] whitespace-nowrap">
                            <NumberFormat
                              value={roundDisplayPrice(
                                extra.price * extra.quantity,
                              )}
                              displayType={"text"}
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale={true}
                              prefix={"₦"}
                            />
                          </p>
                        </div>
                      ))}
                  </div>
                )}
            </div>
            {index !== items.length - 1 && <hr className="border-gray-200" />}
          </React.Fragment>
        );
      });
    };

    const collapseItems = ["same_day", "next_day"]
      .map((groupKey) => {
        const items = groupedItems[groupKey];
        const itemCount = items.length;
        if (itemCount === 0) return null;
        return {
          key: groupKey,
          label:
            groupKey === "same_day" ? (
              <span className="font-bold">
                Receive Today ({itemCount} Item{itemCount !== 1 ? "s" : ""})
              </span>
            ) : (
              <span className="font-bold">
                {normalizeBusinessType(businessType) === "wholesale"
                  ? "Receive at Set Date Below"
                  : "Receive Tomorrow"}{" "}
                ({itemCount} Item
                {itemCount !== 1 ? "s" : ""})
              </span>
            ),
          children: (
            <div>
              {renderItemList(items)}
              {itemCount > 0 && (
                <div className="mt-6 space-y-6">
                  {groupKey === "next_day" && (
                    <div>
                      <h3 className="text-2xl font-semibold mb-3">
                        Delivery Schedule
                      </h3>
                      <DatePicker
                        value={
                          nextDayDeliveryDate
                            ? dayjs(nextDayDeliveryDate)
                            : null
                        }
                        placeholder="Select delivery date *"
                        className="w-full"
                        disabledDate={(current) => {
                          if (!current) return false;

                          const now = dayjs();
                          if (current.isBefore(now.startOf("day"))) return true;

                          if (
                            normalizeBusinessType(businessType) === "retail" ||
                            normalizeBusinessType(businessType) === "restaurant"
                          ) {
                            if (current.day() === 0) return true;

                            const currentHour = now.hour();
                            const tomorrow = now.startOf("day").add(1, "day");
                            const dayAfterTomorrow = now
                              .startOf("day")
                              .add(2, "day");

                            if (currentHour >= 14) {
                              return current.isBefore(dayAfterTomorrow);
                            }
                            return current.isBefore(tomorrow);
                          }

                          if (
                            normalizeBusinessType(businessType) === "wholesale"
                          ) {
                            const earliestWholesaleDate = now
                              .startOf("day")
                              .add(4, "day");
                            const dayOfWeek = current.day();
                            return (
                              current.isBefore(earliestWholesaleDate) ||
                              (dayOfWeek !== 3 && dayOfWeek !== 6)
                            );
                          }

                          return false;
                        }}
                        onChange={(date, dateString) => {
                          setNextDayDeliveryDate(dateString);
                        }}
                        status={
                          nextDayDeliveryMode === DELIVERY_TYPES.DELIVERY &&
                          !nextDayDeliveryDate
                            ? "error"
                            : ""
                        }
                      />
                      {normalizeBusinessType(businessType) === "wholesale" && (
                        <p className="text-lg text-gray-500 font-medium mt-1">
                          Checkout Day (Day 0):{" "}
                          {dayjs().format("ddd MMM D YYYY")} · Delivery Day (Day
                          4+): Wednesdays & Saturdays.
                        </p>
                      )}
                      {nextDayDeliveryDate && (
                        <div className="mt-3 p-3 rounded-lg">
                          <p className="text-2xl font-gilroyBold text-green-500">
                            {normalizeBusinessType(businessType) === "wholesale"
                              ? "Delivery Date:"
                              : "Selected Date:"}{" "}
                            {dayjs(nextDayDeliveryDate).format(
                              "ddd MMM D YYYY",
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    <h3 className="text-2xl font-semibold mb-3">
                      Delivery Options
                    </h3>
                    <Card className="border-0">
                      <Radio.Group
                        value={
                          groupKey === "same_day"
                            ? sameDayDeliveryMode
                            : nextDayDeliveryMode
                        }
                        onChange={
                          groupKey === "same_day"
                            ? handleSameDayDeliveryChange
                            : handleNextDayDeliveryChange
                        }
                        className="w-full"
                      >
                        <div className="space-y-4">
                          <div className="flex justify-between items-center">
                            <Radio
                              value={DELIVERY_TYPES.PICKUP}
                              className="text-2xl"
                            >
                              Pick-up
                            </Radio>
                          </div>
                          <hr className="border-gray-200" />
                          <div className="flex justify-between items-center">
                            <Radio
                              value={DELIVERY_TYPES.DELIVERY}
                              className="text-2xl"
                            >
                              Delivery
                            </Radio>
                            <span className="text-2xl text-[#44843F] font-semibold">
                              <NumberFormat
                                value={roundDisplayPrice(
                                  groupKey === "same_day"
                                    ? calculatedOrderPrice?.same_day
                                        ?.deliveryFee || 0
                                    : calculatedOrderPrice?.next_day
                                        ?.deliveryFee || 0,
                                )}
                                displayType={"text"}
                                thousandSeparator={true}
                                decimalScale={2}
                                fixedDecimalScale={true}
                                prefix={"₦"}
                              />
                            </span>
                          </div>
                        </div>
                      </Radio.Group>
                    </Card>
                  </div>
                </div>
              )}
            </div>
          ),
        };
      })
      .filter(Boolean);

    return (
      <div className="space-y-6">
        <Collapse
          items={collapseItems}
          activeKey={activeCollapseKeys}
          onChange={(keys) => {
            const keysArray = Array.isArray(keys) ? keys : [keys];
            setActiveCollapseKeys(keysArray);
          }}
          accordion={false}
        />
      </div>
    );
  };

  return (
    <Spin spinning={loading} indicator={antIcon}>
      <div className="review-checkout-container">
        <div className="header px-8 md:px-12 py-8">
          <Row justify="center">
            <Col xs={24} lg={22} xl={18}>
              <BackToRestaurant
                businessId={businessId}
                businessType={businessType}
                deliveryType={checkoutDeliveryType}
                targetPage="cart"
              />
              <h1 className="text-4xl md:text-5xl font-bold mt-4">Payment</h1>
            </Col>
          </Row>
        </div>
        <div className="content px-8 md:px-12 pb-24">
          <Row justify="center">
            <Col xs={24} lg={20} xl={16}>
              <div className="space-y-8">
                {normalizeBusinessType(businessType) !== "wholesale" && (
                  <Card className="border-2 rounded-xl bg-[#f5fff5] p-6">
                    <div className="flex items-center gap-6">
                      <img
                        src={restaurantData.bannerImage}
                        alt="restaurant_logo"
                        className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-3xl font-semibold text-gray-900 truncate">
                          {restaurantData.name}
                          {restaurantData.pickupLga ||
                          restaurantData.lga ||
                          restaurantData.business?.lga
                            ? ` - ${restaurantData.pickupLga || restaurantData.lga || restaurantData.business?.lga}`
                            : ""}
                        </p>
                        <p className="text-xl text-gray-600 mt-2">
                          {totalItems} Item{totalItems !== 1 ? "s" : ""} •{" "}
                          <NumberFormat
                            value={roundDisplayPrice(totalAmount)}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={2}
                            fixedDecimalScale={true}
                            prefix={"₦"}
                          />
                        </p>
                      </div>
                    </div>
                  </Card>
                )}

                <Card className="border-2 rounded-xl p-6">
                  <h2 className="text-3xl font-semibold mb-6">Order Items</h2>
                  <OrderItemsSection />
                </Card>

                {showDeliveryAddress && (
                  <>
                    <Card className="border-2 rounded-xl p-6">
                      <div className="flex justify-between items-center mb-6">
                        <h2 className="text-3xl font-semibold">
                          Delivery Address
                        </h2>
                        {allDeliveryLocations.length > 0 && (
                          <button
                            className="text-[#44843F] font-semibold text-xl hover:text-green-700 transition-colors"
                            onClick={showDrawer}
                          >
                            Change
                          </button>
                        )}
                      </div>
                      <AddressManager
                        visible={visibleDrawer}
                        onClose={closeDrawer}
                        onAddressSelect={(address) => {
                          setSelectedAddress(address.id);
                          localStorage.setItem(
                            "konnect_last_selected_address_id",
                            address.id,
                          );
                          setRecalcTrigger((prev) => prev + 1);
                          closeDrawer();
                        }}
                        onAddressUpdate={() => {
                          setRecalcTrigger((prev) => prev + 1);
                          fetchUserAddresses();
                        }}
                        selectedAddressId={selectedAddress}
                        title="Select Delivery Address"
                      />
                      {(() => {
                        const activeAddress =
                          allDeliveryLocations.find(
                            (a) => a.id === selectedAddress,
                          ) || allDeliveryLocations[0];
                        if (!activeAddress) {
                          return (
                            <div className="text-center py-12 bg-gray-50 rounded-lg">
                              <button
                                className="text-[#44843F] font-semibold text-xl hover:text-green-700 transition-colors inline-flex items-center gap-2"
                                onClick={showDrawer}
                              >
                                <PlusOutlined />
                                Add Delivery Address
                              </button>
                            </div>
                          );
                        }

                        return (
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <img
                                  src={map_locationIcon}
                                  alt="Location"
                                  className="w-5 h-5"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="text-xl font-semibold text-gray-900">
                                    {activeAddress?.label}{" "}
                                    {activeAddress?.address}
                                  </h3>
                                  {activeAddress.isDefault && (
                                    <span className="bg-green-500 text-white px-2 py-0.5 rounded text-xs font-medium">
                                      Default
                                    </span>
                                  )}
                                </div>
                                <p className="text-xl text-gray-600">
                                  {[activeAddress.street, activeAddress.address]
                                    .filter(Boolean)
                                    .join(", ")}
                                </p>
                                {activeAddress.phone && (
                                  <p className="text-xl text-gray-500 mt-1">
                                    Phone: {activeAddress.phone}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      {calculatedOrderPrice?.distance && (
                        <div className="mt-6 pt-6 flex space-x-2 border-t border-gray-200">
                          <p className="font-medium text-xl text-gray-700">
                            Estimated Distance from{" "}
                            {normalizeBusinessType(businessType) === "store" ||
                            normalizeBusinessType(businessType) === "retail" ||
                            normalizeBusinessType(businessType) === "wholesale"
                              ? "store"
                              : "restaurant"}
                          </p>
                          <p className="font-semibold text-xl text-gray-900">
                            {calculatedOrderPrice?.distance?.km} km
                          </p>
                        </div>
                      )}
                    </Card>
                  </>
                )}

                <PromoBanner
                  orderCount={weeklyOrderCount}
                  promoCap={rateConfig?.promoCap}
                  rateConfig={rateConfig}
                  businessType={businessType}
                />

                <Card className="border-2 rounded-xl p-6">
                  <div className="space-y-8">
                    <div>
                      <h2 className="text-3xl font-semibold mb-6">
                        Payment Method
                      </h2>
                      <Radio.Group
                        value={paymentMethod}
                        onChange={(event) =>
                          setPaymentMethod(event.target.value)
                        }
                        className="w-full"
                      >
                        <div className="flex justify-between items-center p-6 bg-gray-50 rounded-lg">
                          <Radio value="wallet" className="text-2xl">
                            Wallet
                          </Radio>
                          <p className="text-2xl font-semibold">
                            {!loading ? (
                              <NumberFormat
                                value={+userTransferData?.balance}
                                displayType={"text"}
                                thousandSeparator={true}
                                decimalScale={2}
                                fixedDecimalScale={true}
                                prefix={"₦"}
                              />
                            ) : (
                              "-"
                            )}
                          </p>
                        </div>
                        <div className="flex justify-between items-center p-6 bg-gray-50 rounded-lg mt-4">
                          <Radio value="FOURWARD" className="text-2xl">
                            4Ward
                          </Radio>
                          <p className="text-2xl font-semibold">
                            <NumberFormat
                              value={fourwardBalance}
                              displayType="text"
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale={true}
                              prefix="₦"
                            />
                          </p>
                        </div>
                      </Radio.Group>
                      <p className="text-center text-2xl text-gray-600 mt-4">
                        {paymentMethod === "FOURWARD"
                          ? "4Ward payment held until delivery"
                          : "Wallet payment held in Lien till delivery"}
                      </p>
                    </div>

                    <TopUpWallet userTransferData={userTransferData} />

                    <div className="pt-6 border-t border-gray-200 space-y-4">
                      <div className="flex justify-between items-center">
                        <p className="text-2xl font-medium text-gray-700">
                          Order amount
                        </p>
                        <p className="text-2xl font-semibold">
                          <NumberFormat
                            value={roundDisplayPrice(totalAmount)}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={2}
                            fixedDecimalScale={true}
                            prefix={"₦"}
                          />
                        </p>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-2xl font-medium text-gray-700">
                          Delivery fee
                        </p>
                        <p className="text-2xl text-gray-900">
                          <NumberFormat
                            value={roundDisplayPrice(
                              calculatedOrderPrice?.deliveryFee || 0,
                            )}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={2}
                            fixedDecimalScale={true}
                            prefix={"₦"}
                          />
                        </p>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-2xl font-medium text-gray-700">
                          Service charge
                        </p>
                        <p className="text-2xl text-gray-900">
                          <NumberFormat
                            value={roundDisplayPrice(serviceFee)}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={2}
                            fixedDecimalScale={true}
                            prefix={"₦"}
                          />
                        </p>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-2xl font-medium text-gray-700">
                          {weeklyOrderCount === 0 &&
                          (calculatedOrderPrice?.orderDiscount > 0 ||
                            calculatedOrderPrice?.same_day?.orderDiscount > 0) ? (
                            <>Order Discount</>
                          ) : (
                            "Less order discount"
                          )}
                        </p>
                        <p className="text-2xl text-gray-900">
                          <NumberFormat
                            value={roundDisplayPrice(
                              calculatedOrderPrice?.orderDiscount ||
                                calculatedOrderPrice?.same_day?.orderDiscount ||
                                0,
                            )}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={2}
                            fixedDecimalScale={true}
                            prefix={"₦"}
                          />
                        </p>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-2xl font-medium text-gray-700">
                          Less delivery discount
                        </p>
                        <p className="text-2xl text-gray-900">
                          <NumberFormat
                            value={roundDisplayPrice(
                              calculatedOrderPrice?.deliveryDiscount || 0,
                            )}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={2}
                            fixedDecimalScale={true}
                            prefix={"₦"}
                          />
                        </p>
                      </div>
                      {sameDayDeliveryMode === DELIVERY_TYPES.DELIVERY ||
                      nextDayDeliveryMode === DELIVERY_TYPES.DELIVERY ? (
                        <div className="mt-2">
                          {(calculatedOrderPrice?.breakdown
                            ?.subtotalWithMarkup || 0) <
                          (rateConfig?.freeDeliveryValue ||
                            rateConfig?.restaurantFdValue ||
                            25000) ? (
                            <div className="p-4 bg-orange-50 border border-orange-100 rounded-lg">
                              <p className="text-xl text-orange-700 font-medium text-center">
                                🚚 Add ₦
                                <NumberFormat
                                  value={roundDisplayPrice(
                                    (rateConfig?.freeDeliveryValue ||
                                      rateConfig?.restaurantFdValue ||
                                      25000) -
                                      (calculatedOrderPrice?.breakdown
                                        ?.subtotalWithMarkup || 0),
                                  )}
                                  displayType={"text"}
                                  thousandSeparator={true}
                                />{" "}
                                more to get{" "}
                                <span className="font-bold">FREE DELIVERY</span>
                              </p>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      {calculatedOrderPrice?.useBonusBalance &&
                        (calculatedOrderPrice?.bonusUsed || 0) > 0 && (
                        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-xl text-green-700 font-medium text-center">
                            🎊 We've applied ₦
                            {roundDisplayPrice(
                              calculatedOrderPrice?.bonusUsed || 0,
                            )}{" "}
                            from your bonus balance to give you a better
                            discount!
                          </p>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                        <p className="text-2xl font-bold text-gray-900">
                          Amount to pay
                        </p>
                        <p className="text-2xl font-bold text-[#44843F]">
                          <NumberFormat
                            value={roundDisplayPrice(
                              calculatedOrderPrice?.breakdown?.finalTotal ??
                                calculatedOrderPrice?.finalTotal ??
                                totalAmount +
                                  (calculatedOrderPrice?.deliveryFee || 0) +
                                  serviceFee -
                                  ((calculatedOrderPrice?.orderDiscount || 0) +
                                    (calculatedOrderPrice?.deliveryDiscount ||
                                      0)),
                            )}
                            displayType={"text"}
                            thousandSeparator={true}
                            decimalScale={2}
                            fixedDecimalScale={true}
                            prefix={"₦"}
                          />
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>


                {showPickupAddress && (
                  <>
                    <Card className="border-2 rounded-xl p-6">
                      <h2 className="text-3xl font-semibold mb-6">
                        {restaurantData?.name
                          ? `${restaurantData.name} - Pickup Address`
                          : "Pickup Address"}
                      </h2>
                      <div className="p-6 bg-gray-50 rounded-lg space-y-2">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <img
                              src={map_locationIcon}
                              alt="Location"
                              className="w-4 h-4"
                            />
                          </div>
                          <h3 className="text-2xl font-bold text-[#44843F]">
                            {restaurantData.name}
                          </h3>
                        </div>
                        <p className="text-xl text-gray-700 pl-11">
                          {[
                            normalizeBusinessType(businessType) === "wholesale"
                              ? restaurantData.supplyAddress ||
                                restaurantData.address ||
                                restaurantData.business?.address
                              : restaurantData.pickupAddressString ||
                                restaurantData.address ||
                                restaurantData.business?.address,
                            restaurantData.pickupLga ||
                              restaurantData.lga ||
                              restaurantData.business?.lga,
                            restaurantData.pickupState ||
                              restaurantData.state ||
                              restaurantData.business?.state,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                        {(restaurantData.contactTelephone ||
                          restaurantData.phone) && (
                          <p className="text-lg text-gray-600 pl-11 font-medium">
                            Phone:{" "}
                            {restaurantData.contactTelephone ||
                              restaurantData.phone}
                          </p>
                        )}
                      </div>
                    </Card>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                      <p className="text-xl text-red-700 text-center font-medium">
                        Note: You will be debited if you fail to pickup within
                        hours.
                      </p>
                    </div>
                  </>
                )}

                <div className="pt-8 pb-24">
                  <button
                    onClick={toggleTransCodeModalDisplay}
                    className={` bg-gray-900 hover:bg-gray-800 text-white w-full rounded-xl px-8 py-6 text-2xl font-semibold transition-colors`}
                  >
                    Complete Order
                  </button>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <RequestTransactionCode
        openDrawer={displayTransCodeModal}
        closeDrawer={toggleTransCodeModalDisplay}
        inputCount={4}
        actionFunction={handleMenuOrder}
        stateFunc={setUserPin}
        transChildrenPresent={true}
        hasTransactionPin={userHasPin}
      >
        <div className="px-8 md:px-12 mt-6">
          <div className="flex justify-between items-center py-6">
            <div>
              <h2 className="text-4xl font-bold">Complete Order</h2>
              <p className="text-2xl text-gray-600 mt-2">
                {restaurantData.name}
              </p>
            </div>
            <Cancel
              onClick={toggleTransCodeModalDisplay}
              className="cursor-pointer w-8 h-8"
            />
          </div>
          <hr className="mb-8 border-gray-200" />

          <div className="space-y-8 pr-2">
            {restaurantCarts.some(
              (item) =>
                (item?.detail?.deliveryDateType ||
                  item?.deliveryDateType ||
                  (businessType === "restaurant" ? "same_day" : "next_day")) ===
                "same_day",
            ) && (
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  Receive Today
                </h3>
                <ul className="list-disc ml-8 space-y-3">
                  {restaurantCarts
                    .filter(
                      (item) =>
                        (item?.detail?.deliveryDateType ||
                          item?.deliveryDateType ||
                          (businessType === "restaurant"
                            ? "same_day"
                            : "next_day")) === "same_day",
                    )
                    .map((item, index) => {
                      const extrasIncluded =
                        item.extras && item.extras.length > 0
                          ? " (extras included)"
                          : "";
                      const vendorName =
                        item?.detail?.businessName ||
                        item?.businessName ||
                        item?.detail?.vendorName ||
                        item?.detail?.business?.name;
                      return (
                        <li key={index} className="text-2xl text-gray-700">
                          {item.quantity} pack(s) of {item.detail?.product_name}
                          {extrasIncluded}
                          {vendorName &&
                            (businessType === "wholesale" ||
                              businessType === "raw_food") && (
                              <span className="block text-xl font-semibold text-gray-800 mt-1">
                                Sold by {vendorName}
                              </span>
                            )}
                        </li>
                      );
                    })}
                </ul>
              </div>
            )}

            {restaurantCarts.some(
              (item) =>
                (item?.detail?.deliveryDateType ||
                  item?.deliveryDateType ||
                  (businessType === "restaurant" ? "same_day" : "next_day")) ===
                "next_day",
            ) && (
              <div>
                {nextDayDeliveryDate && (
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">
                    Receive on{" "}
                    {dayjs(nextDayDeliveryDate).format("ddd MMM D YYYY")}
                  </h3>
                )}
                <ul className="list-disc ml-8 space-y-3">
                  {restaurantCarts
                    .filter(
                      (item) =>
                        (item?.detail?.deliveryDateType ||
                          item?.deliveryDateType ||
                          (businessType === "restaurant"
                            ? "same_day"
                            : "next_day")) === "next_day",
                    )
                    .map((item, index) => {
                      const extrasIncluded =
                        item.extras && item.extras.length > 0
                          ? " (extras included)"
                          : "";
                      const vendorName =
                        item?.detail?.businessName ||
                        item?.businessName ||
                        item?.detail?.vendorName ||
                        item?.detail?.business?.name;
                      return (
                        <li key={index} className="text-2xl text-gray-700">
                          {item.quantity} pack(s) of {item.detail?.product_name}
                          {extrasIncluded}
                          {vendorName &&
                            (businessType === "wholesale" ||
                              businessType === "raw_food") && (
                              <span className="block text-xl font-semibold text-gray-800 mt-1">
                                Sold by {vendorName}
                              </span>
                            )}
                        </li>
                      );
                    })}
                </ul>
              </div>
            )}

            <hr className="border-gray-200" />

            <p className="text-2xl text-gray-700">
              {calculatedOrderPrice && restaurantData?.name && (
                <>
                  Please enter your transaction PIN to authorise payment of{" "}
                  <span className="small_currency">NGN</span>{" "}
                  <span className="font-semibold text-[#44843F]">
                    <NumberFormat
                      value={roundDisplayPrice(
                        calculatedOrderPrice?.breakdown?.finalTotal ??
                          calculatedOrderPrice?.finalTotal ??
                          totalAmount +
                            (calculatedOrderPrice?.deliveryFee || 0) +
                            serviceFee -
                            ((calculatedOrderPrice?.orderDiscount || 0) +
                              (calculatedOrderPrice?.deliveryDiscount || 0)),
                      )}
                      displayType={"text"}
                      thousandSeparator={true}
                      decimalScale={2}
                      fixedDecimalScale={true}
                      prefix={"₦"}
                    />
                  </span>
                </>
              )}
            </p>
          </div>
        </div>
      </RequestTransactionCode>

      <Modal
        title="Delivery Unavailable"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalOk}
        okText="Okay"
        cancelButtonProps={{ style: { display: "none" } }}
      >
        <p className="text-2xl">Delivery service will resume soon.</p>
        <p className="text-2xl">
          Collect from the restaurant and enjoy a discount.
        </p>
      </Modal>

      <Modal
        title="Outside Free Delivery Area"
        open={showDistanceCapModal}
        onOk={() => setShowDistanceCapModal(false)}
        onCancel={() => setShowDistanceCapModal(false)}
        okText="Got it"
        cancelButtonProps={{ style: { display: "none" } }}
      >
        <div className="pt-2 space-y-4">
          <p className="text-2xl">
            Hey, your delivery address is outside the Free Delivery area
          </p>
          <p className="text-2xl font-medium text-gray-600 italic">
            Order from a closer{" "}
            {normalizeBusinessType(businessType) === "store" ||
            normalizeBusinessType(businessType) === "retail"
              ? "store"
              : "restaurant"}
          </p>
        </div>
      </Modal>

      <FooterDisplay />
      <HelpSupportButton page="CHECKOUT" businessId={businessId} />
    </Spin>
  );
};

export default ReviewCheckout;
